import React, { useState } from 'react';

import { useSelector } from 'react-redux';

import AddIcon from '@atlaskit/icon/glyph/add';
import SmartUserPicker, {
  Appearance,
  OptionData,
} from '@atlaskit/smart-user-picker';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { ScreenReadersOnly } from 'src/components/accessibility';
import { uncurlyUuid } from 'src/components/analytics';
import { User } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import {
  getCurrentPullRequestId,
  getPullRequestAddReviewerPendingAaids,
} from 'src/redux/pull-request/selectors';
import { getPrReviewGroupsEnabled } from 'src/selectors/feature-selectors';
import {
  getCurrentRepositoryUuid,
  getCurrentRepositoryWorkspaceUUID,
  getIsCurrentRepositoryPrivate,
} from 'src/selectors/repository-selectors';

import { useRecommendedReviewers } from '../../hooks/suggested-reviewers';

import * as styles from './reviewer-smart-picker.style';
import messages from './reviewer-statuses-card.i18n';
import {
  filterOptionsFunction,
  SmartUserPickerSharedProps,
} from './shared-smart-picker-logic';

export const ReviewerSmartPicker = ({
  reviewersAccountIds,
  onSelection,
  repoFullSlug,
  currentUser,
}: {
  reviewersAccountIds: string[];
  onSelection: (userId: OptionData) => void;
  repoFullSlug: string;
  currentUser: User | null | undefined;
}) => {
  const intl = useIntl();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [recommendedReviewers, setRecommendedReviewers] = useState([]);
  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const [workspaceSlug, repoSlug] = repoFullSlug.split('/');
  useRecommendedReviewers(setRecommendedReviewers, workspaceSlug, repoSlug);

  const currentRepositoryUuid = useSelector(getCurrentRepositoryUuid);
  const currentRepositoryWorkspaceUUID = useSelector(
    getCurrentRepositoryWorkspaceUUID
  );
  const isCurrentRepositoryPrivate = useSelector(getIsCurrentRepositoryPrivate);
  const isPrReviewGroupsEnabled = useSelector(getPrReviewGroupsEnabled);
  const currentPullRequestId = useSelector(getCurrentPullRequestId);
  const pendingAaids = useSelector(getPullRequestAddReviewerPendingAaids);
  const currentRepoUuid = uncurlyUuid(currentRepositoryUuid as string);
  const currentWorkspaceUuid = uncurlyUuid(
    currentRepositoryWorkspaceUUID as string
  );

  // User Recommendation Service will return ALL Bitbucket users if repoId (containerId) or workspaceId is empty string or null.
  // If current repo or workspace is not defined in store, we do not want to show the picker since it will have all BBC users included
  if (!currentRepoUuid || !currentWorkspaceUuid) {
    return null;
  }

  const workspaceIds = [currentWorkspaceUuid];
  let isPublicRepo = !isCurrentRepositoryPrivate;
  if (location.hostname.endsWith('devbucket.org')) {
    // Testing reviewers against devbucket is difficult because CPUS doesn't
    // know anything about the local devbucket workspace. Instead, we use an
    // existing integration workspace (`test-workspace`) and mark it as public
    // so that we can add reviewers. As a public repository this should return
    // any integration users, so if you want to test specific permissions you
    // can substitute this with your own integration workspace that already
    // has permissions set up.
    workspaceIds.push('d83d0d95-a100-4926-bdb7-2a83d54e1708');
    isPublicRepo = true;
  }

  const reviewerCardSmartUserPickerProps = {
    containerId: currentRepoUuid,
    filterOptions: filterOptionsFunction(
      isInputEmpty,
      recommendedReviewers,
      currentUser!,
      reviewersAccountIds,
      pendingAaids
    ),
    productAttributes: {
      emailDomain: 'Context',
      isPublicRepo,
      workspaceIds,
      pullRequestId: currentPullRequestId,
    },
    value: [],
    open: isPickerOpen,
    placeholder: intl.formatMessage(messages.reviewersSearchPlaceholder),
    appearance: 'compact' as Appearance,
    onFocus: () => setIsPickerOpen(true),
    onBlur: () => setIsPickerOpen(false),
    onChange: (value: any, _: any) => {
      if (value) {
        onSelection(Array.isArray(value) ? value[0] : value);
      }
    },
    onInputChange: (query: any, _: any) => {
      if (query) {
        setIsInputEmpty(query?.length === 0);
      } else {
        setIsInputEmpty(true);
      }
    },
    styles: {
      container: (css: any) => ({
        ...css,
        width: '100%',
        '> div': {
          height: 'auto',
        },
      }),
      control: (css: any) => ({
        ...css,
        backgroundColor: 'transparent',
        width: '100%',
        '> div > div': {
          paddingLeft: '2px',
        },
      }),
      menu: (css: any) => ({
        ...css,
        zIndex: 10,
        width: '100%',
      }),
      placeholder: (css: any) => ({
        ...css,
        color: colors.N200,
        paddingLeft: '26px !important',
      }),
    },
  };

  return (
    <styles.AddReviewerContainer data-testid="add-reviewer-container">
      <AddIcon
        label={intl.formatMessage(messages.addReviewer)}
        size="small"
        primaryColor={token('color.icon', colors.N200)}
      />
      <ScreenReadersOnly>
        {intl.formatMessage(messages.addReviewer)}
      </ScreenReadersOnly>
      <SmartUserPicker
        {...SmartUserPickerSharedProps}
        {...reviewerCardSmartUserPickerProps}
        {...(isPrReviewGroupsEnabled && { includeGroups: true })}
      />
    </styles.AddReviewerContainer>
  );
};
